<template>
	<div
		:class="`formulate-input-element formulate-input-element--${context.type}`"
		:data-type="context.type">
		<div v-if="context.label && context.labelPosition === 'title'" class="row">
			<div class="col-md">
				<h2>{{ context.label }}</h2>
			</div>
		</div>
		<div v-if="showAddressNameField" class="row">
			<div class="col mb-3">
				<b-form-group
					class="mb-0"
					:label="showFieldLabels ? 'Venue Name' : null">
					<b-form-input
						v-model="form.name"
						name="name"
						:placeholder="showFieldLabels ? null : 'Venue Name'"
						:state="invalidState(context.id + '_addr_name')"
						@input="invalidDirty(context.id + '_addr_name')"></b-form-input>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_addr_name') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</div>
		</div>
		<div class="row">
			<div class="col-md mb-3">
				<b-form-group
					class="mb-0"
					:label="showFieldLabels ? context.label ? context.label : 'Physical Address' : null">
					<b-form-input
						v-model="form.address"
						name="address"
						:placeholder="showFieldLabels ? null : 'Physical Address'"
						:state="invalidState(context.id + '_addr_address')"
						@input="invalidDirty(context.id + '_addr_address')"></b-form-input>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_addr_address') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</div>
		</div>
		<div class="row">
			<div class="col-lg-3">
				<b-form-group
					class="mb-0"
					:label="showFieldLabels ? 'Country' : null">
					<b-form-select
						v-model="form.country"
						name="country"
						:disabled="!exists(countries)"
						:options="countries"
						:state="invalidState(context.id + '_addr_country')"
						@input="invalidDirty(context.id + '_addr_country')">
						<template #first>
							<b-form-select-option :value="null" disabled>-- Select Country --</b-form-select-option>
						</template>
					</b-form-select>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_addr_country') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</div>
			<div class="col-lg-3">
				<b-form-group
					class="mb-0"
					:label="showFieldLabels ? 'State' : null">
					<b-form-select
						v-model="form.state"
						name="state"
						:disabled="! form.country || !exists(states)"
						:options="selectStates"
						:state="invalidState(context.id + '_addr_state')"
						@input="invalidDirty(context.id + '_addr_state')">
						<template #first>
							<b-form-select-option :value="null" disabled>-- Select {{ countryKey }} --</b-form-select-option>
						</template>
					</b-form-select>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_addr_state') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</div>
			<div class="col-lg-3">
				<b-form-group
					class="mb-0"
					:label="showFieldLabels ? 'City' : null">
					<b-form-input
						v-model.trim="form.city"
						name="city"
						:placeholder="showFieldLabels ? null : 'City'"
						:state="invalidState(context.id + '_addr_city')"
						@input="invalidDirty(context.id + '_addr_city')"></b-form-input>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_addr_city') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</div>
			<div class="col-lg-3">
				<b-form-group
					class="mb-0"
					:label="showFieldLabels ? 'Postal Code' : null">
					<b-form-input
						v-model.trim="form.zip"
						name="zip"
						:placeholder="showFieldLabels ? null : 'Postal Code'"
						:state="invalidState(context.id + '_addr_zip')"
						@input="invalidDirty(context.id + '_addr_zip')"></b-form-input>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_addr_zip') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</div>
		</div>
	</div>
</template>
<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import formInvalid from '@/Pages/Forms/Mixins/FormInvalid'

export default {
	mixins: [formInvalid],
	props: {
		context: {
			type: Object,
			required: true
		}
	},
	data () {
		return {
			updatingContext: false, // updating from direct user input?
			form: {
				name: null,
				address: null,
				country: null,
				city: null,
				zip: null,
				state: null
			}
		}
	},
	computed: {
		...mapState({
			addressInit: state => state.init.address,
			submitted: state => state.application.submitted
		}),
		states () {
			return this.addressInit.states ?? {}
		},
		countries () {
			return this.addressInit[this.allowedCountries] ?? {}
		},
		countryKey () {
			if (! this.form.country || Object.keys(this.states).length <= 0) {
				return 'State'
			}

			return Object.keys(this.states[this.form.country])[0]
		},
		selectStates () {
			if (! this.form.country || Object.keys(this.states).length <= 0) {
				return
			}

			return this.states[this.form.country][this.countryKey]
		},
		showFieldLabels () {
			return this.context.labelPosition === 'fields'
		},
		showAddressNameField () {
			return this.context.attributes.hasOwnProperty('with-address-name')
				&& this.context.attributes['with-address-name'] === true
		},
		allowedCountries () {
			if (this.context.attributes['all-countries'] === undefined) {
				return 'allowed_countries'
			}

			return this.context.attributes['all-countries'] === true ? 'countries' : 'allowed_countries'
		}
	},
	watch: {
		'context.model': {
			handler: function () {
				this.updateFromContext()
			},
			deep: true
		},
		form: {
			handler: _.debounce(function (formData) {
				this.updatingContext = true
				const modelData = Object.assign({}, formData)
				this.$set(this.context, 'model', modelData)

				// Validate
				for (const field in this.form) {
					if (!this.form.hasOwnProperty(field)) continue
					if (! this.showAddressNameField && field === 'name') continue

					const validationName = field[0].toUpperCase() + field.substring(1)
					this.validateRequired(validationName, this.context.id + '_addr_' + field, formData[field])
				}

				this.updatingContext = false
			}, 250),
			immediate: true,
			deep: true
		},
		submitted () {
			for (const field in this.form) {
				if (!this.form.hasOwnProperty(field)) continue
				this.$store.commit('setInvalidDirty', this.context.id + '_addr_' + field)
			}
		},
		'form.country': {
			handler: _.debounce(function (newValue, oldValue) {
				if (newValue !== null && oldValue !== null && newValue !== oldValue && this.state !== null) {
					this.form.state = null
				}
			}, 240),
			immediate: true
		}
	},
	beforeDestroy () {
		for (const field in this.form) {
			if (!this.form.hasOwnProperty(field)) continue
			this.invalidReset(this.context.id + '_addr_' + field)
		}
	},
	mounted () {
		this.updateFromContext()
	},
	methods: {
		updateFromContext () {
			if (this.updatingContext) {
				return
			}

			if (typeof this.context.model !== 'object') {
				this.$set(this.context, 'model', this.form)
			}

			const modelData = Object.assign({}, this.context.model)
			for (const formKey in this.form) {
				if (! this.form.hasOwnProperty(formKey)) continue
				if (! modelData.hasOwnProperty(formKey)) continue
				this.form[formKey] = modelData[formKey]
			}
		}
	}
}
</script>
